/**
 * @typedef {Object} SchedulerExecutionDailyDocLike
 * @property {string} [date] YYYY-MM-DD
 * @property {Object} [metrics]
 * @property {Object} [metrics.totals]
 * @property {unknown} [metrics.totals.outcomeMatchCount]
 * @property {unknown} [metrics.totals.outcomeNoMatchCount]
 * @property {unknown} [metrics.totals.failedCount]
 * @property {Object} [metrics.byCrawlType]
 */

/**
 * @typedef {Object} SchedulerExecutionOutcomeChartRow
 * @property {string} dateKey
 * @property {string} dateLabelShort
 * @property {number} match
 * @property {number} noMatch
 * @property {number} failed
 */

/**
 * @typedef {Object} SchedulerExecutionOutcomeLabels
 * @property {string} match
 * @property {string} noMatch
 * @property {string} failed
 */

/**
 * @typedef {Object} SchedulerExecutionOutcomeFacet
 * @property {string} crawlType
 * @property {string} title
 * @property {SchedulerExecutionOutcomeLabels} outcomeLabels
 * @property {SchedulerExecutionOutcomeChartRow[]} rows
 */

const KNOWN_CRAWL_TYPE_FACET_ORDER = ['google_maps_search', 'socials'];

const CRAWL_TYPE_FACET_TITLES = {
  google_maps_search: 'Google Maps search',
  socials: 'Socials',
  unknown: 'Unknown',
  all: 'All crawl types',
};

const DEFAULT_OUTCOME_LABELS = {
  match: 'Match',
  noMatch: 'No match',
  failed: 'Failed',
};

const SOCIALS_OUTCOME_LABELS = {
  match: 'Socials found',
  noMatch: 'None found',
  failed: 'Failed',
};

/** @param {unknown} value */
export function coerceNonNegativeInt(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 0;
  }
  return Math.floor(parsed);
}

/** @param {unknown} dateKey */
export function formatMonthDayLabel(dateKey) {
  if (!dateKey || typeof dateKey !== 'string') {
    return '';
  }
  const parts = dateKey.split('-');
  if (parts.length !== 3) {
    return '';
  }
  return `${parts[1]}-${parts[2]}`;
}

/**
 * Recharts Tooltip labelFormatter — prefer full `dateKey` from the first series payload when present.
 *
 * @param {string} label
 * @param {unknown} payload
 * @returns {string}
 */
export function formatOutcomeExecutionTooltipLabel(label, payload) {
  const dateKeyFromPayload = payload?.[0]?.payload?.dateKey;
  if (typeof dateKeyFromPayload === 'string' && dateKeyFromPayload.length > 0) {
    return dateKeyFromPayload;
  }
  return label;
}

/** @param {unknown} value */
function readObjectRecord(value) {
  return value && typeof value === 'object' ? value : {};
}

/** @param {SchedulerExecutionDailyDocLike} dailyDoc */
function readTotalsRecord(dailyDoc) {
  return readObjectRecord(readObjectRecord(dailyDoc.metrics).totals);
}

/** @param {SchedulerExecutionDailyDocLike} dailyDoc */
function readByCrawlTypeRecord(dailyDoc) {
  return readObjectRecord(readObjectRecord(dailyDoc.metrics).byCrawlType);
}

/**
 * @param {readonly SchedulerExecutionDailyDocLike[]} firestoreDailyDocs
 * @returns {SchedulerExecutionDailyDocLike[]}
 */
function sortDailyDocsAscending(firestoreDailyDocs) {
  return [...firestoreDailyDocs].sort((documentA, documentB) => {
    const left = documentA.date ?? '';
    const right = documentB.date ?? '';
    return left.localeCompare(right);
  });
}

/**
 * @param {SchedulerExecutionDailyDocLike} dailyDoc
 * @param {Record<string, unknown>} countsRecord
 * @returns {SchedulerExecutionOutcomeChartRow}
 */
function toOutcomeChartRow(dailyDoc, countsRecord) {
  const dateKey = typeof dailyDoc.date === 'string' ? dailyDoc.date : '';
  return {
    dateKey,
    dateLabelShort: formatMonthDayLabel(dateKey),
    match: coerceNonNegativeInt(countsRecord.outcomeMatchCount),
    noMatch: coerceNonNegativeInt(countsRecord.outcomeNoMatchCount),
    failed: coerceNonNegativeInt(countsRecord.failedCount),
  };
}

/** @param {string} crawlType */
export function formatCrawlTypeFacetTitle(crawlType) {
  const knownTitle = CRAWL_TYPE_FACET_TITLES[crawlType];
  if (typeof knownTitle === 'string') {
    return knownTitle;
  }
  return crawlType;
}

/** @param {string} crawlType */
export function outcomeLabelsForCrawlType(crawlType) {
  if (crawlType === 'socials') {
    return SOCIALS_OUTCOME_LABELS;
  }
  return DEFAULT_OUTCOME_LABELS;
}

/**
 * @param {readonly SchedulerExecutionDailyDocLike[]} firestoreDailyDocs
 * @returns {string[]}
 */
function collectCrawlTypeFacetKeys(firestoreDailyDocs) {
  const extraKeys = new Set();
  firestoreDailyDocs.forEach(function collectExtraCrawlTypes(dailyDoc) {
    Object.keys(readByCrawlTypeRecord(dailyDoc)).forEach(function addExtraKey(crawlTypeKey) {
      if (crawlTypeKey.length === 0) {
        return;
      }
      if (KNOWN_CRAWL_TYPE_FACET_ORDER.includes(crawlTypeKey)) {
        return;
      }
      extraKeys.add(crawlTypeKey);
    });
  });
  return [...KNOWN_CRAWL_TYPE_FACET_ORDER, ...[...extraKeys].sort()];
}

/**
 * @param {readonly SchedulerExecutionDailyDocLike[]} firestoreDailyDocs
 * @returns {boolean}
 */
function documentsHaveByCrawlType(firestoreDailyDocs) {
  return firestoreDailyDocs.some(function hasCrawlTypeBreakdown(dailyDoc) {
    return Object.keys(readByCrawlTypeRecord(dailyDoc)).length > 0;
  });
}

/**
 * @param {readonly SchedulerExecutionDailyDocLike[]} firestoreDailyDocs
 * @returns {boolean}
 */
function documentsLackByCrawlType(firestoreDailyDocs) {
  return firestoreDailyDocs.some(function missingCrawlTypeBreakdown(dailyDoc) {
    return Object.keys(readByCrawlTypeRecord(dailyDoc)).length === 0;
  });
}

/**
 * @param {readonly SchedulerExecutionDailyDocLike[]} sortedAsc
 * @returns {SchedulerExecutionOutcomeFacet}
 */
function buildTotalsFacet(sortedAsc) {
  return {
    crawlType: 'all',
    title: CRAWL_TYPE_FACET_TITLES.all,
    outcomeLabels: DEFAULT_OUTCOME_LABELS,
    rows: sortedAsc.map(function mapTotalsRow(dailyDoc) {
      return toOutcomeChartRow(dailyDoc, readTotalsRecord(dailyDoc));
    }),
  };
}

/**
 * @param {readonly SchedulerExecutionDailyDocLike[]} sortedAsc
 * @returns {SchedulerExecutionOutcomeFacet[]}
 */
function buildCrawlTypeFacets(sortedAsc) {
  return collectCrawlTypeFacetKeys(sortedAsc).map(function mapCrawlTypeFacet(crawlType) {
    return {
      crawlType,
      title: formatCrawlTypeFacetTitle(crawlType),
      outcomeLabels: outcomeLabelsForCrawlType(crawlType),
      rows: sortedAsc.map(function mapPerTypeRow(dailyDoc) {
        const perTypeRecord = readByCrawlTypeRecord(dailyDoc)[crawlType];
        return toOutcomeChartRow(dailyDoc, readObjectRecord(perTypeRecord));
      }),
    };
  });
}

/**
 * Rows for Recharts ascending by calendar date (oldest first).
 *
 * @param {readonly SchedulerExecutionDailyDocLike[]} firestoreDailyDocs
 * @returns {SchedulerExecutionOutcomeChartRow[]}
 */
export function buildSchedulerExecutionDailyChartRowsAsc(firestoreDailyDocs) {
  const sortedAsc = sortDailyDocsAscending(firestoreDailyDocs);
  return sortedAsc.map(function mapDailyDocument(dailyDoc) {
    return toOutcomeChartRow(dailyDoc, readTotalsRecord(dailyDoc));
  });
}

/**
 * Small-multiple facets: Google Maps search and socials first, then any extra crawl types.
 * Legacy rollups without `metrics.byCrawlType` fall back to a single totals facet.
 * Mixed windows keep that totals facet so days without a breakdown are not shown as zeros.
 *
 * @param {readonly SchedulerExecutionDailyDocLike[]} firestoreDailyDocs
 * @returns {SchedulerExecutionOutcomeFacet[]}
 */
export function buildSchedulerExecutionOutcomeFacets(firestoreDailyDocs) {
  const sortedAsc = sortDailyDocsAscending(firestoreDailyDocs);
  if (sortedAsc.length === 0) {
    return [];
  }

  const hasSegmentedDocuments = documentsHaveByCrawlType(sortedAsc);
  if (!hasSegmentedDocuments) {
    return [buildTotalsFacet(sortedAsc)];
  }

  const crawlTypeFacets = buildCrawlTypeFacets(sortedAsc);
  if (documentsLackByCrawlType(sortedAsc)) {
    return [buildTotalsFacet(sortedAsc), ...crawlTypeFacets];
  }
  return crawlTypeFacets;
}
